import isEqual from 'lodash/isEqual';

import { trimToNull } from '~/utils/stringUtils';

export type FrequentFlyerForm = {
  airline?: string | null;
  number?: string | null;
};

export type TravelPreferencesForm = {
  frequent_flyer_numbers?: Array<FrequentFlyerForm> | null;
  seat_preference?: string | null;
  special_requests?: string | null;
};

export type CustomerPropertyPatch = {
  key: string;
  value: unknown;
};

export type CustomerPropertyValue = {
  key: string;
  value: unknown;
};

export const TRAVEL_PREFERENCE_KEYS = ['frequent_flyer_numbers', 'seat_preference', 'special_requests'] as const;

export const FREQUENT_FLYER_AIRLINES = [
  'Virgin Velocity',
  'Singapore KrisFlyer',
  'Qantas',
  'Emirates Skywards',
  'Qatar Privilege Club',
  'American Airlines AAdvantage',
  'Delta SkyMiles',
  'United MileagePlus',
  'Cathay Pacific',
  'Air New Zealand',
  'Thai Airways',
] as const;

export const TRAVEL_PREFERENCE_LIMITS = {
  maxFrequentFlyerRows: 20,
  airlineMaxLength: 64,
  numberMaxLength: 32,
  seatPreferenceMaxLength: 255,
  specialRequestsMaxLength: 2000,
} as const;

type FrequentFlyerRowErrors = {
  airline?: string;
  number?: string;
};

type TravelPreferencesErrors = {
  frequentFlyerRows: Record<number, FrequentFlyerRowErrors>;
  frequentFlyerNumbers?: string;
  seatPreference?: string;
  specialRequests?: string;
};

const isFrequentFlyerRowEmpty = (row: FrequentFlyerForm) => !trimToNull(row?.airline) && !trimToNull(row?.number);

export function propertiesToTravelPreferencesForm(
  properties?: Array<CustomerPropertyValue> | null,
): TravelPreferencesForm {
  const byKey = new Map((properties ?? []).map((property) => [property.key, property.value]));

  const frequentFlyerValue = byKey.get('frequent_flyer_numbers');
  const frequentFlyerNumbers = Array.isArray(frequentFlyerValue)
    ? frequentFlyerValue.map((entry) => ({
        airline: typeof entry?.airline === 'string' ? entry.airline : null,
        number: typeof entry?.number === 'string' ? entry.number : null,
      }))
    : [];

  const seatPreference = byKey.get('seat_preference');
  const specialRequests = byKey.get('special_requests');

  return {
    frequent_flyer_numbers: frequentFlyerNumbers,
    seat_preference: typeof seatPreference === 'string' ? seatPreference : null,
    special_requests: typeof specialRequests === 'string' ? specialRequests : null,
  };
}

/**
 * Builds the PATCH body for every travel preference key. null values will delete the property.
 */
export function travelPreferencesToPropertyPatches(
  preferences?: TravelPreferencesForm | null,
): Array<CustomerPropertyPatch> {
  const frequentFlyerNumbers = (preferences?.frequent_flyer_numbers ?? [])
    .filter((row) => !isFrequentFlyerRowEmpty(row))
    .map((row) => ({ airline: trimToNull(row.airline), number: trimToNull(row.number) }))
    .filter((row): row is { airline: string; number: string } => Boolean(row.airline && row.number));

  return [
    {
      key: 'frequent_flyer_numbers',
      value: frequentFlyerNumbers.length > 0 ? frequentFlyerNumbers : null,
    },
    { key: 'seat_preference', value: trimToNull(preferences?.seat_preference) },
    { key: 'special_requests', value: trimToNull(preferences?.special_requests) },
  ];
}

export function diffTravelPreferencePatches(
  next?: TravelPreferencesForm | null,
  previous?: TravelPreferencesForm | null,
): Array<CustomerPropertyPatch> {
  const nextPatches = travelPreferencesToPropertyPatches(next);
  const previousByKey = new Map(travelPreferencesToPropertyPatches(previous).map((patch) => [patch.key, patch.value]));

  return nextPatches.filter((patch) => !isEqual(patch.value, previousByKey.get(patch.key)));
}

export function validateTravelPreferences(preferences?: TravelPreferencesForm | null): TravelPreferencesErrors {
  const rows = preferences?.frequent_flyer_numbers ?? [];
  const errors: TravelPreferencesErrors = { frequentFlyerRows: {} };
  const duplicateRows = new Set(findDuplicateFrequentFlyerRows(rows));

  rows.forEach((row, index) => {
    if (isFrequentFlyerRowEmpty(row)) {
      return;
    }

    const airline = trimToNull(row.airline);
    const number = trimToNull(row.number);
    const rowErrors: FrequentFlyerRowErrors = {};

    if (!airline) {
      rowErrors.airline = 'Select or enter a frequent flyer program';
    } else if (airline.length > TRAVEL_PREFERENCE_LIMITS.airlineMaxLength) {
      rowErrors.airline = `Must be ${TRAVEL_PREFERENCE_LIMITS.airlineMaxLength} characters or fewer`;
    } else if (duplicateRows.has(index)) {
      rowErrors.airline = 'Each program may only appear once';
    }

    if (!number) {
      rowErrors.number = 'Enter a membership number';
    } else if (number.length > TRAVEL_PREFERENCE_LIMITS.numberMaxLength) {
      rowErrors.number = `Must be ${TRAVEL_PREFERENCE_LIMITS.numberMaxLength} characters or fewer`;
    }

    if (Object.keys(rowErrors).length > 0) {
      errors.frequentFlyerRows[index] = rowErrors;
    }
  });

  const filledRows = rows.filter((row) => !isFrequentFlyerRowEmpty(row));
  if (filledRows.length > TRAVEL_PREFERENCE_LIMITS.maxFrequentFlyerRows) {
    errors.frequentFlyerNumbers = `Add no more than ${TRAVEL_PREFERENCE_LIMITS.maxFrequentFlyerRows} programs`;
  }

  const seatPreference = trimToNull(preferences?.seat_preference);
  if (seatPreference && seatPreference.length > TRAVEL_PREFERENCE_LIMITS.seatPreferenceMaxLength) {
    errors.seatPreference = `Must be ${TRAVEL_PREFERENCE_LIMITS.seatPreferenceMaxLength} characters or fewer`;
  }

  const specialRequests = trimToNull(preferences?.special_requests);
  if (specialRequests && specialRequests.length > TRAVEL_PREFERENCE_LIMITS.specialRequestsMaxLength) {
    errors.specialRequests = `Must be ${TRAVEL_PREFERENCE_LIMITS.specialRequestsMaxLength} characters or fewer`;
  }

  return errors;
}

/**
 * Airlines the API would reject as duplicates, compared the same way it compares them: trimmed and
 * case-insensitive. Returns the row indexes so the error can be attached to the offending rows.
 */
function findDuplicateFrequentFlyerRows(rows?: Array<FrequentFlyerForm> | null): Array<number> {
  const seen = new Map<string, number>();
  const duplicates: Array<number> = [];

  (rows ?? []).forEach((row, index) => {
    const airline = trimToNull(row?.airline)?.toLowerCase();
    if (!airline) {
      return;
    }
    if (seen.has(airline)) {
      duplicates.push(index);
      return;
    }
    seen.set(airline, index);
  });

  return duplicates;
}
